/* BOJOURNAL — gallery-corridor.js
   <gallery-corridor>: the walk past the paintings.
   - On screens from 750px, and when motion is welcome, the section is made
     as tall as the corridor is long; its stage holds still under the
     header while the page scrolls, and the track moves sideways by the same
     distance — so scrolling down walks along the wall. In Arabic the walk
     goes the other way.
   - Otherwise the corridor is simply swiped (native horizontal scroll).
   - Keyboard focus on a work scrolls the page to bring it into view.
   - Photos lose their plain margin (Bojournal.trimArt), so only the
     painting hangs on the wall — never stretched.
   - The painting in the photo has the last word on its shape: a size typed
     the wrong way round (60 × 40 for an upright work) is turned, on the
     wall and on its label; and when the size still does not match the
     painting, its long side is kept and the short side follows the
     painting, so no work is ever cut. */
(() => {
  const B = window.Bojournal;
  if (!B || !B.Component || customElements.get('gallery-corridor')) return;

  const WALK = window.matchMedia('(min-width: 750px) and (prefers-reduced-motion: no-preference)');
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  class GalleryCorridor extends B.Component {
    init() {
      this.stage = this.querySelector('[data-corridor-stage]');
      this.view = this.querySelector('[data-corridor-view]');
      this.track = this.querySelector('[data-corridor-track]');
      this.meter = this.querySelector('[data-corridor-meter]');
      if (!this.stage || !this.view || !this.track) return;
      this.rtl = getComputedStyle(this).direction === 'rtl';
      this.frame = 0;
      this.distance = 0;

      this.querySelectorAll('.corridor__work:not(.corridor__work--todo)').forEach((work) => this.hang(work));

      const schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };
      this.on(window, 'scroll', schedule, { passive: true });
      this.on(this.view, 'scroll', schedule, { passive: true });
      this.on(window, 'resize', () => this.measure());
      this.on(WALK, 'change', () => this.measure());
      this.on(this, 'focusin', (event) => this.bringIntoView(event.target));
      if ('ResizeObserver' in window) {
        this.resizer = new ResizeObserver(() => this.measure());
        this.resizer.observe(this.track);
      }
      this.measure();
    }

    teardown() {
      if (this.resizer) this.resizer.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    /* Finds the painting in the photo (Bojournal.artBox on a small copy),
       gives the frame its shape, then trims the photo to it. When the photo
       has no margin, or cannot be read, the whole photo is the painting. */
    hang(work) {
      const img = work.querySelector('img');
      if (!img) return;
      const src = img.currentSrc || img.src;
      if (!src) {
        img.addEventListener('load', () => this.hang(work), { once: true });
        return;
      }
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.decoding = 'async';
      probe.onload = () => {
        let box = null;
        try {
          box = B.artBox ? B.artBox(probe) : null;
        } catch (error) {
          box = null; // not readable across origins
        }
        if (!box && probe.naturalWidth && probe.naturalHeight) box = { ratio: probe.naturalWidth / probe.naturalHeight };
        this.fit(work, box);
        if (B.trimArt) B.trimArt(work, img);
      };
      try {
        const url = new URL(src, window.location.href);
        if (url.searchParams.has('width')) url.searchParams.set('width', '240');
        probe.src = url.toString();
      } catch (error) {
        probe.src = src;
      }
    }

    /* The frame follows the painting (box.ratio: its width / height). */
    fit(work, box) {
      const item = work.closest('[data-corridor-item]');
      const ratio = box && box.ratio;
      if (!item || !ratio || !Number.isFinite(ratio)) return;
      let w = parseFloat(item.style.getPropertyValue('--w'));
      let h = parseFloat(item.style.getPropertyValue('--h'));
      if (!(w > 0 && h > 0)) return;
      const TURN = 1.1;
      const shape = w / h;
      if ((ratio > TURN && shape < 1 / TURN) || (ratio < 1 / TURN && shape > TURN)) {
        [w, h] = [h, w];
        // The label reads as the wall shows it: the two numbers swapped.
        const size = item.querySelector('[data-corridor-size]');
        if (size) {
          const parts = size.textContent.split('×').map((part) => part.trim());
          if (parts.length === 2) size.textContent = parts[1] + ' × ' + parts[0];
        }
        item.setAttribute('data-corridor-turned', '');
      }
      // Still not the painting's shape: keep the long side, fit the other.
      if (Math.abs(Math.log(ratio / (w / h))) > Math.log(1.04)) {
        if (w >= h) h = w / ratio;
        else w = h * ratio;
      }
      item.style.setProperty('--w', w.toFixed(1));
      item.style.setProperty('--h', h.toFixed(1));
      this.rescale();
    }

    /* One scale for the wall: the tallest work hung at 150 cm, and never
       lower than a 280 cm gallery wall (as the section computes it). */
    rescale() {
      const wall = this.closest('.corridor');
      if (!wall) return;
      let tallest = 0;
      this.querySelectorAll('[data-corridor-item]').forEach((item) => {
        tallest = Math.max(tallest, parseFloat(item.style.getPropertyValue('--h')) || 0);
      });
      if (tallest > 0) wall.style.setProperty('--wall-cm', String(Math.max(280, Math.round(tallest / 2 + 195))));
    }

    get stickyTop() {
      return parseFloat(getComputedStyle(this.stage).top) || 0;
    }

    measure() {
      const walking = WALK.matches;
      this.toggleAttribute('data-pinned', walking);
      if (walking) {
        this.distance = Math.max(0, this.track.offsetWidth - this.view.clientWidth);
        this.style.height = Math.round(this.stage.offsetHeight + this.distance) + 'px';
      } else {
        this.distance = 0;
        this.style.removeProperty('height');
        this.track.style.removeProperty('transform');
      }
      this.update();
    }

    update() {
      let progress = 0;
      if (this.hasAttribute('data-pinned')) {
        const top = this.getBoundingClientRect().top;
        progress = this.distance > 0 ? clamp((this.stickyTop - top) / this.distance, 0, 1) : 0;
        // Whole pixels keep the labels' text crisp.
        const x = Math.round(progress * this.distance);
        this.track.style.transform = 'translate3d(' + (this.rtl ? x : -x) + 'px, 0, 0)';
        // Focus can scroll a clipped box on its own; the track alone moves.
        if (this.view.scrollLeft) this.view.scrollLeft = 0;
      } else {
        const max = this.view.scrollWidth - this.view.clientWidth;
        progress = max > 0 ? clamp(Math.abs(this.view.scrollLeft) / max, 0, 1) : 0;
      }
      if (this.meter) this.meter.style.setProperty('--p', progress.toFixed(4));
    }

    /* The page scroll that centres an item of the track in the stage. */
    bringIntoView(target) {
      if (!this.hasAttribute('data-pinned')) return;
      const item = target.closest('.corridor__track > li');
      if (!item) return;
      const start = this.rtl ? this.track.offsetWidth - item.offsetLeft - item.offsetWidth : item.offsetLeft;
      const x = clamp(start + item.offsetWidth / 2 - this.view.clientWidth / 2, 0, this.distance);
      const sectionTop = window.scrollY + this.getBoundingClientRect().top - this.stickyTop;
      window.scrollTo({ top: Math.round(sectionTop + x), behavior: 'auto' });
      this.update();
    }
  }

  B.define('gallery-corridor', GalleryCorridor);
})();
